import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import OtpVerifyPage from "@/pages/OtpVerifyPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Proposals from "@/pages/Proposals";
import NewProposal from "@/pages/NewProposal";
import ProposalEdit from "@/pages/ProposalEdit";
import PublicProposal from "@/pages/PublicProposal";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Invoices from "@/pages/Invoices";
import NewInvoice from "@/pages/NewInvoice";
import EditInvoice from "@/pages/EditInvoice";
import PublicInvoice from "@/pages/PublicInvoice";
import PublicDelivery from "@/pages/PublicDelivery";
import ClientPortal from "@/pages/ClientPortal";
import Settings from "@/pages/Settings";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><div className="animate-pulse-gentle font-display text-subhead text-ink-muted">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Safety net: if the user lands anywhere after a Google OAuth attempt but
  // without OAuth hash params (Supabase redirected to site root silently),
  // oauth_mode will still be set in localStorage. Detect this and redirect
  // to login with an error instead of silently showing the current page.
  useEffect(() => {
    // /auth/callback is handled entirely by AuthCallback — skip here.
    // (AuthCallback clears the hash synchronously before this effect runs,
    // so checking the hash here would always see it empty on the callback page.)
    if (location.pathname === '/auth/callback') return;

    const hasOAuthHash = window.location.hash.includes('access_token=')
      || window.location.hash.includes('session_id=')
      || window.location.hash.includes('error=');
    const orphanedMode = localStorage.getItem('oauth_mode');

    // oauth_mode set but no OAuth hash = Supabase redirected to the wrong page
    if (orphanedMode && !hasOAuthHash) {
localStorage.removeItem('oauth_mode');
      const target = orphanedMode === 'signup' ? '/signup' : '/login';
      sessionStorage.setItem('auth_error', 'Google sign-in failed. Please try again.');
      navigate(target, { replace: true });
    }
  }, [location.pathname, navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for Supabase OAuth params — handles cases where Supabase
  // redirects to the site root instead of /auth/callback (e.g. callback URL not
  // in the Supabase allowed-redirects list).
  if (location.hash?.includes('access_token=') || location.hash?.includes('session_id=') || location.hash?.includes('error=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-otp" element={<OtpVerifyPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public routes */}
      <Route path="/p/:token" element={<PublicProposal />} />
      <Route path="/pay/:token" element={<PublicInvoice />} />
      <Route path="/delivery/:token" element={<PublicDelivery />} />
      <Route path="/portal/:token" element={<ClientPortal />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Layout><Clients /></Layout></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><Layout><ClientDetail /></Layout></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><Layout><Proposals /></Layout></ProtectedRoute>} />
      <Route path="/proposals/new" element={<ProtectedRoute><Layout><NewProposal /></Layout></ProtectedRoute>} />
      <Route path="/proposals/:id/edit" element={<ProtectedRoute><Layout><ProposalEdit /></Layout></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Layout><Projects /></Layout></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><Layout><ProjectDetail /></Layout></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Layout><Invoices /></Layout></ProtectedRoute>} />
      <Route path="/invoices/new" element={<ProtectedRoute><Layout><NewInvoice /></Layout></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><Layout><EditInvoice /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
